import React, { useState, useEffect } from 'react';
import '../styles/preloader.css';

const Preloader = ({ onLoadComplete, preloadComponents = [] }) => {
  const [progress, setProgress] = useState(0);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let loadedResources = 0;
    let totalResources = 0;
    let simulatedProgress = 0;
    let progressInterval;
    let imagesLoaded = false;
    let fontsLoaded = false;
    let componentsLoaded = false;
    let minTimeElapsed = false;

    // Minimum loading time for better UX (500ms - reduced for faster initial render)
    const minLoadTime = setTimeout(() => {
      minTimeElapsed = true;
      checkIfReady();
    }, 500);

    // Simulate initial progress
    progressInterval = setInterval(() => {
      if (simulatedProgress < 20) {
        simulatedProgress += 1;
        setProgress(simulatedProgress);
      }
    }, 30);

    // Skip component preloading - components are now lazy loaded on demand
    componentsLoaded = true;
    simulatedProgress = Math.max(simulatedProgress, 40);
    setProgress(simulatedProgress);

    // Check if fonts are loaded
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        fontsLoaded = true;
        simulatedProgress = Math.max(simulatedProgress, 60);
        setProgress(simulatedProgress);
        checkIfReady();
      }).catch(() => {
        fontsLoaded = true;
        checkIfReady();
      });
    } else {
      fontsLoaded = true;
    }

    // Wait a bit for DOM to be ready, then track images
    setTimeout(() => {
      const images = Array.from(document.querySelectorAll('img'));
      const criticalImages = images.filter(img => 
        !img.loading || img.loading !== 'lazy'
      );

      totalResources = criticalImages.length || 1;

      if (criticalImages.length === 0) {
        imagesLoaded = true;
        simulatedProgress = Math.max(simulatedProgress, 85);
        setProgress(simulatedProgress);
        checkIfReady();
      } else {
        criticalImages.forEach((img) => {
          if (img.complete && img.naturalHeight !== 0) {
            loadedResources++;
            updateProgress();
          } else {
            img.addEventListener('load', () => {
              loadedResources++;
              updateProgress();
            });
            img.addEventListener('error', () => {
              loadedResources++;
              updateProgress();
            });
          }
        });
      }
    }, 500);

    function updateProgress() {
      const imageProgress = (loadedResources / totalResources) * 35 + 65;
      simulatedProgress = Math.max(simulatedProgress, Math.floor(imageProgress));
      setProgress(simulatedProgress);
      
      if (loadedResources >= totalResources) {
        imagesLoaded = true;
        checkIfReady();
      }
    }

    function checkIfReady() {
      if (imagesLoaded && fontsLoaded && componentsLoaded && minTimeElapsed) {
        clearInterval(progressInterval);
        
        // Animate to 100%
        let finalProgress = simulatedProgress;
        const finalInterval = setInterval(() => {
          finalProgress += 5;
          setProgress(Math.min(finalProgress, 100));
          
          if (finalProgress >= 100) {
            clearInterval(finalInterval);
            setProgress(100);
            setIsReady(true);
            if (onLoadComplete) onLoadComplete();
          }
        }, 30);
      }
    }

    // Fallback: force ready after 10 seconds
    const fallbackTimeout = setTimeout(() => {
      if (!isReady) {
        clearInterval(progressInterval);
        setProgress(100);
        setIsReady(true);
        if (onLoadComplete) onLoadComplete();
      }
    }, 10000);

    return () => {
      clearTimeout(minLoadTime);
      clearTimeout(fallbackTimeout);
      clearInterval(progressInterval);
    };
  }, [onLoadComplete, preloadComponents]);

  return (
    <div className="preloader">
      <div className="preloader-content">
        <div className="preloader-logo">
          <div className="preloader-logo-text">SITCORP</div>
          <div className="preloader-tagline">International</div>
        </div>
        
        <div className="preloader-bar-container">
          <div className="preloader-bar">
            <div 
              className="preloader-bar-fill" 
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="preloader-percentage">{progress}%</div>
        </div>

        <div className="preloader-loading-text">
          {progress < 30 ? 'Initializing...' :
           progress < 60 ? 'Loading components...' :
           progress < 85 ? 'Loading assets...' :
           progress < 100 ? 'Almost ready...' :
           'Welcome'}
        </div>
      </div>
    </div>
  );
};

export default Preloader;
